import { Bell, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

/**
 * AlertsPage - Alert Configuration and Rule Management
 * 
 * This page allows users to:
 * - Configure alert rules and thresholds
 * - Set up notification channels (email, SMS, webhooks)
 * - Manage alert escalation workflows
 * - Define alert conditions and triggers
 * - Test and preview alert configurations
 * 
 * Note: User notifications are in the Notifications page under Administration
 */
export function AlertsPage() {
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="alert-configuration"
        icon={Bell}
        title="Alert Configuration Coming Soon"
        description="Configure alert rules, thresholds, notification channels, and escalation workflows."
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Alert Configuration</h1>
          <p className="text-neutral-400 mt-1">
            Configure alert rules, thresholds, and notification channels
          </p>
        </div>
        <button className="btn-primary">
          Create Alert Rule
        </button>
      </div>

      {/* Alert Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Active Rules</p>
              <p className="text-2xl font-bold text-white mt-1">23</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Bell className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Critical Rules</p>
              <p className="text-2xl font-bold text-danger-500 mt-1">8</p>
            </div>
            <div className="p-3 bg-danger-500/20 rounded-lg">
              <AlertTriangle className="text-danger-500" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Acknowledged</p>
              <p className="text-2xl font-bold text-success-500 mt-1">45</p>
            </div>
            <div className="p-3 bg-success-500/20 rounded-lg">
              <CheckCircle className="text-success-500" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Info</p>
              <p className="text-2xl font-bold text-white mt-1">28</p>
            </div>
            <div className="p-3 bg-neutral-700/20 rounded-lg">
              <Info className="text-neutral-400" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Alerts List */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Active Alerts</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-4 p-4 bg-danger-500/10 border border-danger-500/30 rounded-lg">
            <AlertTriangle className="text-danger-500 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="text-white font-medium">Critical: Security vulnerability detected</h4>
              <p className="text-sm text-neutral-400 mt-1">CVE-2024-12345 found in production environment - Immediate action required</p>
              <p className="text-xs text-neutral-500 mt-2">2 hours ago • Risk ID: RISK-001</p>
            </div>
            <button className="btn-primary text-sm px-4 py-1.5">Acknowledge</button>
          </div>
          <div className="flex items-start gap-4 p-4 bg-danger-500/10 border border-danger-500/30 rounded-lg">
            <AlertTriangle className="text-danger-500 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="text-white font-medium">Critical: Compliance deadline approaching</h4>
              <p className="text-sm text-neutral-400 mt-1">DORA assessment due in 3 days - 12 controls pending review</p>
              <p className="text-xs text-neutral-500 mt-2">5 hours ago • Framework: DORA</p>
            </div>
            <button className="btn-primary text-sm px-4 py-1.5">Acknowledge</button>
          </div>
          <div className="flex items-start gap-4 p-4 bg-warning-500/10 border border-warning-500/30 rounded-lg">
            <AlertTriangle className="text-warning-500 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="text-white font-medium">High: Control test failure</h4>
              <p className="text-sm text-neutral-400 mt-1">CTRL-004 incident response test failed - Remediation plan needed</p>
              <p className="text-xs text-neutral-500 mt-2">1 day ago • Control ID: CTRL-004</p>
            </div>
            <button className="btn-outline text-sm px-4 py-1.5">View Details</button>
          </div>
          <div className="flex items-start gap-4 p-4 bg-neutral-900/50 border border-neutral-800 rounded-lg">
            <CheckCircle className="text-success-500 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="text-white font-medium">Info: Risk assessment completed</h4>
              <p className="text-sm text-neutral-400 mt-1">Q4 2024 supplier risk assessment finished - 3 new risks identified</p>
              <p className="text-xs text-neutral-500 mt-2">2 days ago • Assessment ID: ASS-089</p>
            </div>
            <span className="text-xs text-success-500">Acknowledged</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
